import { Outlet, useParams, useSearchParams } from 'react-router-dom'

import { Box, Button, Grid } from '@factorialco/deprecated-design-system'
import {
  PerformanceReviewProcessReviewSettingsPageDocument,
  PerformanceStartReviewProcessValidatorFailedCheckEnum,
} from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { EditionWindow } from 'modules/performance_process/components/review/EditionWindow/EditionWindow'
import { ConfigurationComponent } from 'modules/performance_process/components/review/EditionWindow/configurationComponent'
import {
  type AvailableSections,
  settingsSections,
} from 'modules/performance_process/components/review/EditionWindow/editionWindowOptions'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'

const isOptionCompleted = (
  optionValue: string,
  validationErrors: PerformanceStartReviewProcessValidatorFailedCheckEnum[]
) => {
  switch (optionValue) {
    case 'basic':
      return !validationErrors.includes('missing_title')
    case 'timeline':
      return !validationErrors.includes('missing_deadline')
    default:
      return true
  }
}

const ReviewSettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { reviewId } = useParams()
  const { navigation } = useNavigation()

  const handleOnChange = (value: string) => {
    setSearchParams({ section: value })
  }
  const { company } = useCurrentSession()
  const { data, error, loading } = useQuery(PerformanceReviewProcessReviewSettingsPageDocument, {
    variables: {
      id: Number(reviewId),
      companyId: company.id,
    },
  })

  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()

  const features = keyBy(data?.features.featuresConnection.nodes || [], 'name')
  const isConnectGoalsAndReviewsV2Enabled =
    features['dev_connect_goals_and_reviews_v2']?.enabled || false
  const isLegacyJobCatalogEnabled = features['legacy_job_catalog']?.enabled || false
  const isJobCatalogTreeEnabled = !isLegacyJobCatalogEnabled
  const isEsignatureEnabled = features['esignature']?.enabled ?? false

  const selectedSection = searchParams.get('section') ?? 'basic'
  const isDraft = data?.performance?.reviewProcess?.status === 'draft'

  const hideSections: AvailableSections[] = isDraft
    ? ['target', 'questions_view']
    : ['target_group']
  const settingOptions = settingsSections(hideSections)
  const review = data?.performance?.reviewProcess

  return (
    <ResponsivePage.Body
      error={error || !review}
      loading={loading}
      showHeader={false}
      widget={
        <Button.Router
          to={navigation.performanceProcess.reviews.review.fullPath({ reviewId: Number(reviewId) })}
        >
          {i18n.t('performance_process.reviews.go_to_overview_button')}
        </Button.Router>
      }
    >
      {review && (
        <>
          <Grid variant="fixed">
            <Box gridColSpan={{ xs: 4 }} gap="s8">
              {settingOptions.map((option) => {
                const completed =
                  review?.startValidationErrors != null &&
                  (!review.startValidationErrors.length ||
                    isOptionCompleted(option.value, review.startValidationErrors))

                return (
                  <EditionWindow
                    key={option.label}
                    label={option.label}
                    value={option.value}
                    selected={option.value === selectedSection}
                    icon={option.icon}
                    onChange={handleOnChange}
                    complete={completed}
                  />
                )
              })}
            </Box>
            <Box padding="s16" gridColSpan={{ s: 6 }}>
              <ConfigurationComponent
                selected={selectedSection}
                review={review}
                isConnectGoalsAndReviewsV2Enabled={isConnectGoalsAndReviewsV2Enabled}
                hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
                isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
                isEsignatureEnabled={isEsignatureEnabled}
              />
            </Box>
          </Grid>
          <Outlet />
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default ReviewSettingsPage
